import { EditOutlined, FieldTimeOutlined, LoadingOutlined } from '@ant-design/icons'
import { Button, Card, Col, Row, Select, Spin } from 'antd'
import AddVacation from 'components/Modals/Vacations/AddVacation'
import UpdateVacationStatus from 'components/Modals/Vacations/UpdateVacationStatus'
import dayjs from 'dayjs'
import UseGetMyVacation from 'hooks/query/workSchedule/UseGetMyVacation'
import UseGetVacationList from 'hooks/query/workSchedule/UseGetVacationList'
import i18next, { t } from 'i18next'
import { UserModel } from 'models/users'
import { VacationModel } from 'models/workSchedule'
import User from 'pages/users/user'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isOperation, isTechnical } from 'utils/helpers/check-roles'
import { UserData } from 'utils/helpers/encrypt-decrypt-user'

interface TechsSetting {
  loadingMore: boolean
  data: VacationModel[]
  search: string
}

const options: {key: string; value: number}[] = [
    {
        key: 'annual',
        value: 1
    },
    {
        key: 'sick',
        value: 2
    },
    {
        key: 'hourly',
        value: 3
    },
    {
        key: 'other',
        value: 4
    },
];

const vacationStatus: {key: string; value: number}[] = [
  {
    key: 'pending',
    value: 1
  },
  {
    key: 'approved', 
    value: 2
  },
  {
    key: "rejected",
    value: 3
  },
]

const VacationRequests = () => {
  const [User, setUser] = useState<UserModel | null>(null);
  const [openAddVication, setOpenAddVication] = useState<boolean>(false)
  const [selectedVacation, setSelectedVacation] = useState<VacationModel | null>(null);
  const [openApproveVacation, setOpenApproveVacation] = useState<boolean>(false)
  const [techsSetting, setTechsSetting] = useState<TechsSetting>({
    loadingMore: false,
    data: [],
    search: "",
  })
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined);
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const userExists = User
  const myVacationQuery = UseGetMyVacation(Boolean(userExists && isTechnical(User?.role_id!)));
  const vacationListQuery = UseGetVacationList(
    {     
      search: techsSetting.search,
      status: statusFilter
    },
    Boolean(userExists && !isTechnical(User?.role_id!))
  );

  const Users = isTechnical(User?.role_id!) ? myVacationQuery : vacationListQuery;

  useEffect(() => {
    setUser(UserData.Decrypt());
  }, []);

  useEffect(() => {
    if (Users.status === "success") {
      const allUsers = Users.data.pages.flatMap(p => p?.data || [])
      setTechsSetting(prev => ({
        ...prev,
        loadingMore: Users.hasNextPage === false,
        data: allUsers,
      }))
    }
  }, [Users.data?.pages])

  useEffect(() => {
    if (techsSetting.loadingMore) return
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        if (Users.hasNextPage) {
          Users.fetchNextPage()
        }
      }
    }, { rootMargin: '100px' })
    if (sentinelRef.current) observer.observe(sentinelRef.current)
    return () => {
      if (sentinelRef.current) observer.unobserve(sentinelRef.current)
    }
  }, [Users.hasNextPage])

  return (
    <div>
      <div style={{display: "flex", gap: "5px", flexWrap: "wrap"}}>
        <Button
          onClick={() => setOpenAddVication(true)}
          type="primary"
          icon={<FieldTimeOutlined />}
          style={{ display: isTechnical(User?.role_id!) ? "flex" : "none", gap: "5px"}}
        >
          {t("add_vacation")}
        </Button>
      </div>
      <Select
          allowClear
          placeholder={t("vacation_status")}
          style={{ width: 200 }}
          onChange={(value) => setStatusFilter(value)}
          options={vacationStatus.map(option => ({
            label: t(option.key),
            value: option.value
          }))}
        />
        <Row gutter={[16,16]} style={{textAlign: i18next.language === "en" ? "left" : "right", marginTop: "10px"}}>
          {techsSetting.data.map((element: VacationModel, index: any) => (
              <Col key={index} md={8} sm={12} xs={24}>
                <Card style={{height: "100%"}} title={
                  <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <p style={{margin: "0px"}}>{element?.user_name}</p>
                    {isOperation(User?.role_id!)
                      ?                     
                      <div 
                      onClick={()=> {setOpenApproveVacation(true); setSelectedVacation(element)}}
                      style={{
                        backgroundColor: "#28befa", 
                        width: "fit-content", 
                        padding: "3px 5px", 
                        borderRadius: "5px", color: "white",
                        cursor: "pointer"
                        }}>
                        <EditOutlined />
                      </div>
                      :<></>
                    }
                  </div>
                } variant="borderless">
                  <div>
                    <p>{t("start_datetime")} : {element?.start_datetime
                    ? i18next.language === "en" 
                      ? dayjs(element.start_datetime).format("YYYY-MM-DD HH:mm:ss")
                      : dayjs(element.start_datetime).format("DD-MM-YYYY HH:mm:ss")
                    : "-"}</p>
                    <p>{t("end_datetime")} : {element?.end_datetime
                      ? i18next.language === "en" 
                        ? dayjs(element.end_datetime).format("YYYY-MM-DD HH:mm:ss")
                        : dayjs(element.end_datetime).format("DD-MM-YYYY HH:mm:ss") 
                      : "-"}
                    </p>
                    <p>
                      {options.map((option, index) => (
                        option.value === element.vacation_type ? `${t("vacation_type")} : ${t(option.key)}` : ""
                      ))}
                    </p>
                    <p>
                      {vacationStatus.map((option, index) => (
                        option.value === element.status ? `${t("vacation_status")} : ${t(option.key)}` : ""
                      ))}
                    </p>
                  </div>
                </Card>
              </Col>
          ))}
        </Row>
        <div ref={sentinelRef}></div>
        {(Users.isLoading || Users.isFetching) 
          ?       
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            </div>
          : <></>  
        }


      <AddVacation
          openAddVication={openAddVication}
          setOpenAddVication={setOpenAddVication}
          selectedVacation={selectedVacation}
          setSelectedVacation={setSelectedVacation}
        />
      <UpdateVacationStatus
          openApproveVacation={openApproveVacation}
          setOpenApproveVacation={setOpenApproveVacation}
          selectedVacation={selectedVacation}
          setSelectedVacation={setSelectedVacation}
        />
    </div>
  )
}

export default VacationRequests